import React, { useState, useEffect } from 'react'
import axios from 'axios'

interface ResultItem {
  part: string
  description: string
  primary_source: string
  lead_time: string
  on_hand: string
  demand_13_wks: string
  buffer_stock: string
  category: string
  reasoning: string
  success: string
  error: string
}

interface ResultsSummary {
  total: number
  category_counts: {
    IGNORE: number
    CRITICAL: number
    [key: string]: number
  }
  successful: number
  failed: number
  processing_duration_seconds: number
}

interface ResultsData {
  job_id: string
  results: ResultItem[]
  summary: ResultsSummary
}

interface ResultsModalProps {
  jobId: string
  isOpen: boolean
  onClose: () => void
}

const ResultsModal: React.FC<ResultsModalProps> = ({ jobId, isOpen, onClose }) => {
  const [data, setData] = useState<ResultsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  useEffect(() => {
    if (isOpen && jobId) {
      fetchResults()
    }
  }, [isOpen, jobId])

  const fetchResults = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get(`/jobs/${jobId}/results`)
      setData(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch results')
      console.error('Failed to fetch results:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'CRITICAL':
        return '#e74c3c'
      case 'CONSTRAINT':
        return '#f39c12'
      case 'IGNORE':
        return 'rgb(87, 130, 43)'
      default:
        return '#667eea'
    }
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Shortage Summary</h2>
            <p style={styles.jobIdText}>Job ID: {jobId}</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {loading ? (
            <div style={styles.centerText}>Loading results...</div>
          ) : error ? (
            <div style={styles.errorMessage}>Error: {error}</div>
          ) : data ? (
            <>
              {/* Summary Section */}
              <div style={styles.summarySection}>
                <h3 style={styles.sectionTitle}>Summary</h3>
                <div style={styles.summaryGrid}>
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>Total Parts</div>
                    <div style={styles.summaryValue}>{data.summary.total}</div>
                  </div>
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>Successful</div>
                    <div style={{ ...styles.summaryValue, color: 'rgb(87, 130, 43)' }}>
                      {data.summary.successful}
                    </div>
                  </div>
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>Failed</div>
                    <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
                      {data.summary.failed}
                    </div>
                  </div>
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>Critical</div>
                    <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
                      {data.summary.category_counts.CRITICAL || 0}
                    </div>
                  </div>
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>Ignore</div>
                    <div style={{ ...styles.summaryValue, color: 'rgb(87, 130, 43)' }}>
                      {data.summary.category_counts.IGNORE || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Results List */}
              <div style={styles.resultsSection}>
                <h3 style={styles.sectionTitle}>Detailed Results</h3>
                <div style={styles.resultsList}>
                  {data.results.map((result, index) => (
                    <div key={index} style={styles.resultItem}>
                      {/* Result Header - Clickable */}
                      <div
                        style={{
                          ...styles.resultHeader,
                          borderLeftColor: getCategoryColor(result.category),
                        }}
                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                      >
                        <div style={styles.resultHeaderLeft}>
                          <div style={styles.partNumber}>{result.part}</div>
                          <div style={styles.partDescription}>{result.description}</div>
                        </div>
                        <div style={styles.resultHeaderRight}>
                          <span
                            style={{
                              ...styles.categoryBadge,
                              backgroundColor: getCategoryColor(result.category),
                            }}
                          >
                            {result.category}
                          </span>
                          <span style={styles.expandIcon}>
                            {expandedIndex === index ? '▼' : '▶'}
                          </span>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedIndex === index && (
                        <div style={styles.resultDetails}>
                          <div style={styles.detailsGrid}>
                            <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Primary Source:</span>
                              <span style={styles.detailValue}>{result.primary_source}</span>
                            </div>
                            <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Lead Time:</span>
                              <span style={styles.detailValue}>{result.lead_time} days</span>
                            </div>
                            <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>On Hand:</span>
                              <span style={styles.detailValue}>{result.on_hand}</span>
                            </div>
                            <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Demand (13 wks):</span>
                              <span style={styles.detailValue}>{result.demand_13_wks}</span>
                            </div>
                            <div style={styles.detailItem}>
                              <span style={styles.detailLabel}>Buffer Stock:</span>
                              <span style={styles.detailValue}>{result.buffer_stock}</span>
                            </div>
                          </div>

                          {/* Reasoning Section */}
                          <div style={styles.reasoningSection}>
                            <h4 style={styles.reasoningTitle}>Reasoning:</h4>
                            <p style={styles.reasoningText}>{result.reasoning}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={styles.centerText}>No data available</div>
          )}
        </div>
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '25px 30px',
    borderBottom: '2px solid #e0e0e0',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#002b49',
  },
  jobIdText: {
    margin: '8px 0 0 0',
    fontSize: '12px',
    fontWeight: '500',
    color: '#666',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#999',
    padding: '0 5px',
    lineHeight: 1,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '25px 30px',
  },
  centerText: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
    fontSize: '16px',
  },
  errorMessage: {
    backgroundColor: '#fadbd8',
    color: '#c0392b',
    padding: '15px',
    borderRadius: '6px',
    border: '1px solid #e74c3c',
  },
  summarySection: {
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
    marginTop: 0,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px',
    marginBottom: '30px',
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  summaryLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#002b49',
  },
  resultsSection: {
    marginTop: '30px',
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  resultItem: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    borderLeft: '4px solid',
    borderLeftColor: '#667eea',
  },
  resultHeaderLeft: {
    flex: 1,
  },
  resultHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  partNumber: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#002b49',
    marginBottom: '5px',
  },
  partDescription: {
    fontSize: '12px',
    color: '#666',
  },
  categoryBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
  },
  expandIcon: {
    fontSize: '12px',
    color: '#999',
  },
  resultDetails: {
    padding: '15px',
    backgroundColor: 'white',
    borderTop: '1px solid #e0e0e0',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '15px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    marginBottom: '5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#333',
  },
  reasoningSection: {
    marginTop: '15px',
    paddingTop: '15px',
    borderTop: '1px solid #e0e0e0',
  },
  reasoningTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    margin: '0 0 10px 0',
    textTransform: 'uppercase',
  },
  reasoningText: {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.6',
    margin: 0,
  },
}

export default ResultsModal
